
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';

export interface PolicyAcceptanceRecord {
  id: string;
  userId?: string;
  userEmail: string;
  userType: string;
  termsAccepted: boolean;
  privacyAccepted: boolean;
  cookieAccepted: boolean;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
}

export const recordPolicyAcceptance = async (
  userEmail: string,
  userType: string,
  termsAccepted: boolean,
  privacyAccepted: boolean,
  cookieAccepted: boolean
): Promise<void> => {
  const timestamp = new Date().toISOString();
  const ipAddress = await fetchUserIp();
  const userAgent = navigator.userAgent;

  try {
    // Get current authenticated user id if available
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    // Format data according to Supabase schema
    const policyData = {
      user_id: userId || null,
      user_email: userEmail,
      user_type: userType,
      terms_accepted: termsAccepted,
      privacy_accepted: privacyAccepted,
      cookie_accepted: cookieAccepted,
      timestamp: timestamp,
      ip_address: ipAddress || null,
      user_agent: userAgent || null
    };

    // Insert into Supabase
    const { error } = await supabase
      .from('policy_acceptances')
      .insert([policyData]);

    if (error) {
      console.error('Error recording policy acceptance:', error);
      // Fall back to localStorage
      storeInLocalStorage(userEmail, userType, termsAccepted, privacyAccepted, cookieAccepted, timestamp, ipAddress, userAgent, userId);
    }
  } catch (error) {
    console.error('Failed to record policy acceptance:', error);
    // Fall back to localStorage
    storeInLocalStorage(userEmail, userType, termsAccepted, privacyAccepted, cookieAccepted, timestamp, ipAddress, userAgent);
  }
};

// Helper function to store in localStorage as fallback
const storeInLocalStorage = (
  userEmail: string,
  userType: string,
  termsAccepted: boolean,
  privacyAccepted: boolean,
  cookieAccepted: boolean,
  timestamp: string,
  ipAddress?: string,
  userAgent?: string,
  userId?: string
) => {
  const records = getPolicyAcceptanceRecords();
  const newRecord: PolicyAcceptanceRecord = {
    id: uuidv4(),
    userEmail,
    userType,
    termsAccepted,
    privacyAccepted,
    cookieAccepted,
    timestamp,
    ipAddress,
    userAgent,
    userId
  };
  
  records.unshift(newRecord);
  localStorage.setItem('policyAcceptanceRecords', JSON.stringify(records));
};

export const getPolicyAcceptanceRecords = (): PolicyAcceptanceRecord[] => {
  // Try to fetch from Supabase first
  try {
    // We'll use localStorage as a cache still since this is a read operation
    return JSON.parse(localStorage.getItem('policyAcceptanceRecords') || '[]');
  } catch (error) {
    console.error('Error parsing policy acceptance records:', error);
    return [];
  }
};

export const checkDuplicateEmail = async (email: string): Promise<boolean> => {
  try {
    const { data: candidates, error: candidatesError } = await supabase
      .from('candidates')
      .select('email')
      .eq('email', email)
      .maybeSingle();
      
    if (!candidatesError && candidates) {
      return true;
    }
    
    const { data: employers, error: employersError } = await supabase
      .from('employers')
      .select('email')
      .eq('email', email)
      .maybeSingle();
      
    if (!employersError && employers) {
      return true;
    }
    
    // Fallback to localStorage
    const existingUsers = JSON.parse(localStorage.getItem('users') || '[]');
    return existingUsers.some((user: any) => user.email === email);
  } catch (error) {
    console.error('Error checking for duplicate email:', error);
    
    // Fallback to localStorage
    const existingUsers = JSON.parse(localStorage.getItem('users') || '[]');
    return existingUsers.some((user: any) => user.email === email);
  }
};

export const checkDuplicatePhone = async (phone: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('candidates')
      .select('phone')
      .eq('phone', phone)
      .maybeSingle();
      
    if (!error && data) {
      return true;
    }
    
    // Fallback to localStorage
    const existingUsers = JSON.parse(localStorage.getItem('users') || '[]');
    return existingUsers.some((user: any) => user.phone === phone);
  } catch (error) {
    console.error('Error checking for duplicate phone:', error);
    
    // Fallback to localStorage
    const existingUsers = JSON.parse(localStorage.getItem('users') || '[]');
    return existingUsers.some((user: any) => user.phone === phone);
  }
};

export const registerUser = async (userData: any): Promise<void> => {
  try {
    // First try to create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password || uuidv4(), // Use provided password or generate one
      options: {
        data: {
          name: userData.name,
          phone: userData.phone
        }
      }
    });
    
    if (!authError && authData.user) {
      // Then create candidate profile
      const { error: profileError } = await supabase
        .from('candidates')
        .insert([{
          id: uuidv4(),
          user_id: authData.user.id,
          email: userData.email,
          name: userData.name,
          phone: userData.phone,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);
        
      if (profileError) {
        console.error('Error creating candidate profile:', profileError);
        // Fall back to localStorage
        storeUserInLocalStorage(userData);
      }
    } else if (authError) {
      console.error('Error creating auth user:', authError);
      // Fall back to localStorage
      storeUserInLocalStorage(userData);
    }
  } catch (error) {
    console.error('Error registering user:', error);
    // Fall back to localStorage
    storeUserInLocalStorage(userData);
  }
};

// Helper function to store user in localStorage as fallback
const storeUserInLocalStorage = (userData: any) => {
  const existingUsers = JSON.parse(localStorage.getItem('users') || '[]');
  existingUsers.push(userData);
  localStorage.setItem('users', JSON.stringify(existingUsers));
};

const fetchUserIp = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error fetching IP:', error);
    return '127.0.0.1';
  }
};
